import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiJson, fetchAuthedBlob } from '../../api/client.js';
import { FormIcon } from '../FormIcon.jsx';
import { FormMenu } from '../FormMenu.jsx';
import { SubmissionStatusPill } from '../FormBadges.jsx';
import { downloadBlob, formatBytes, formatDay, formatDateTime, formatTime } from '../formSchema.js';

const FILTERS = [
  ['all', 'All'],
  ['new', 'New'],
  ['reviewed', 'Reviewed'],
  ['late', 'Late'],
];

function daysLate(submittedAt, dueDate) {
  if (!submittedAt || !dueDate) return 0;
  const due = new Date(`${dueDate}T23:59:59`);
  const diff = new Date(submittedAt).getTime() - due.getTime();
  return diff > 0 ? Math.ceil(diff / 86400000) : 0;
}

export function FormSubmissionsTab({ form, onCountChange, onExport, onError }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('open');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [counts, setCounts] = useState({ all: 0, new: 0, reviewed: 0, late: 0 });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const countRef = useRef(onCountChange);
  countRef.current = onCountChange;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ status: filter, page: String(page) });
    if (debounced) qs.set('q', debounced);
    setLoading(true);
    return apiJson(`/api/admin/forms/${form.id}/submissions?${qs}`)
      .then((data) => {
        setRows(data.data || []);
        setMeta(data.meta || {});
        setCounts(data.counts || {});
        countRef.current?.(data.counts?.all ?? 0, data.counts?.new ?? 0);
      })
      .catch((e) => onError(e.message))
      .finally(() => setLoading(false));
  }, [form.id, filter, page, debounced, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => setSelected(new Set()), [filter, page, debounced]);

  const openDetail = useCallback(
    (id) => {
      apiJson(`/api/admin/forms/${form.id}/submissions/${id}`)
        .then((data) => setDetail(data.submission))
        .catch((e) => onError(e.message));
    },
    [form.id, onError],
  );

  useEffect(() => {
    if (openId) openDetail(openId);
  }, [openId, openDetail]);

  const closeDetail = () => {
    setDetail(null);
    if (openId) {
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  };

  const updateOne = async (id, body) => {
    try {
      const data = await apiJson(`/api/admin/forms/${form.id}/submissions/${id}`, { method: 'PUT', body });
      if (detail?.id === id) setDetail(data.submission);
      void load();
      return data.submission;
    } catch (e) {
      onError(e.message);
      return null;
    }
  };

  const bulk = async (action, ids = [...selected]) => {
    if (!ids.length) return;
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} submission(s)? Uploaded files are removed too.`)) return;
    try {
      await apiJson(`/api/admin/forms/${form.id}/submissions/bulk`, { method: 'POST', body: { ids, action } });
      setSelected(new Set());
      if (action === 'delete' && detail && ids.includes(detail.id)) closeDetail();
      void load();
    } catch (e) {
      onError(e.message);
    }
  };

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="fm-subs">
      <div className="fm-toolbar">
        <div className="fm-chips">
          {FILTERS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`fm-chip${filter === id ? ' is-on' : ''}`}
              onClick={() => {
                setFilter(id);
                setPage(1);
              }}
            >
              {label}
              {counts[id] ? <span className="fm-chip-count">{counts[id]}</span> : null}
            </button>
          ))}
        </div>
        <label className="fm-search">
          <FormIcon name="search" size={16} />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, email or answers..."
          />
        </label>
      </div>

      {selected.size ? (
        <div className="fm-bulkbar">
          <strong>{selected.size} selected</strong>
          <button type="button" className="fm-btn fm-btn--sm" onClick={() => bulk('reviewed')}>
            <FormIcon name="check" size={14} /> Mark reviewed
          </button>
          <button type="button" className="fm-btn fm-btn--sm" onClick={() => bulk('new')}>
            Mark as new
          </button>
          <button type="button" className="fm-btn fm-btn--sm" onClick={() => onExport(`?ids=${[...selected].join(',')}`)}>
            <FormIcon name="download" size={14} /> Export selected
          </button>
          <button type="button" className="fm-btn fm-btn--sm fm-btn--outline-danger" onClick={() => bulk('delete')}>
            <FormIcon name="trash" size={14} /> Delete
          </button>
          <button type="button" className="fm-link-btn" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      ) : null}

      <div className="fm-card fm-table-card">
        <table className="fm-table">
          <thead>
            <tr>
              <th className="fm-check-cell">
                <input type="checkbox" checked={allOnPage} onChange={toggleAll} aria-label="Select all on this page" />
              </th>
              <th>Submitted By</th>
              <th>Client</th>
              <th>Project</th>
              <th>Submitted</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`is-link${detail?.id === row.id ? ' is-current' : ''}`} onClick={() => openDetail(row.id)}>
                <td className="fm-check-cell" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggle(row.id)} aria-label={`Select submission ${row.id}`} />
                </td>
                <td>
                  <div className="fm-person">
                    <strong>{row.submitter_name || row.submitter_email || 'Anonymous'}</strong>
                    {row.submitter_name && row.submitter_email ? <small>{row.submitter_email}</small> : null}
                  </div>
                </td>
                <td className="fm-muted-cell">{row.client_name || (row.source === 'portal' ? '—' : 'Website')}</td>
                <td className="fm-muted-cell">{row.project_name || '—'}</td>
                <td>
                  <div className="fm-when">
                    {formatDay(row.created_at)}
                    <small>{formatTime(row.created_at)}</small>
                  </div>
                </td>
                <td>
                  <div className="fm-status-stack">
                    <SubmissionStatusPill status={row.status} />
                    {row.is_late ? <span className="fm-late">Late</span> : null}
                    {row.file_count ? (
                      <span className="fm-meta-chip" title={`${row.file_count} file(s)`}>
                        <FormIcon name="file" size={13} /> {row.file_count}
                      </span>
                    ) : null}
                    {row.has_notes ? (
                      <span className="fm-meta-chip" title="Has internal notes">
                        <FormIcon name="note" size={13} />
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="fm-actions-cell" onClick={(e) => e.stopPropagation()}>
                  <FormMenu
                    label={`Actions for submission ${row.id}`}
                    items={[
                      { label: 'View submission', icon: 'eye', onClick: () => openDetail(row.id) },
                      row.status === 'reviewed'
                        ? { label: 'Mark as new', icon: 'inbox', onClick: () => updateOne(row.id, { status: 'new' }) }
                        : { label: 'Mark as reviewed', icon: 'check', onClick: () => updateOne(row.id, { status: 'reviewed' }) },
                      { label: 'Export row (CSV)', icon: 'download', onClick: () => onExport(`?ids=${row.id}`) },
                      { label: 'Delete', icon: 'trash', danger: true, onClick: () => bulk('delete', [row.id]) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && !rows.length ? <p className="fm-empty">Loading submissions…</p> : null}
        {!loading && !rows.length ? (
          <div className="fm-empty">
            <FormIcon name="inbox" size={28} />
            <p>{filter !== 'all' || debounced ? 'No submissions match this view.' : form.status === 'draft' ? 'Publish the form to start collecting submissions.' : 'No submissions yet.'}</p>
          </div>
        ) : null}
        {meta.last_page > 1 ? (
          <footer className="fm-pager">
            <span>
              Page {meta.current_page} of {meta.last_page} · {meta.total} submissions
            </span>
            <div className="fm-pager-btns">
              <button type="button" className="fm-icon-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                <FormIcon name="chevronLeft" size={16} />
              </button>
              <button type="button" className="fm-icon-btn" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <FormIcon name="chevronRight" size={16} />
              </button>
            </div>
          </footer>
        ) : null}
      </div>

      {detail ? <SubmissionDrawer key={detail.id} submission={detail} onClose={closeDetail} onUpdate={updateOne} onDelete={() => bulk('delete', [detail.id])} onError={onError} /> : null}
    </div>
  );
}

function AnswerValue({ answer, onError }) {
  if (answer.type === 'file') {
    if (!answer.files?.length) return <span className="fm-empty-answer">No file uploaded</span>;
    return (
      <ul className="fm-answer-files">
        {answer.files.map((file) => (
          <li key={file.id}>
            <FormIcon name="forms" size={16} />
            <span>
              {file.name}
              <small>{formatBytes(file.size)}</small>
            </span>
            <button
              type="button"
              className="fm-btn fm-btn--sm"
              onClick={() =>
                fetchAuthedBlob(file.url)
                  .then((blob) => downloadBlob(blob, file.name))
                  .catch((e) => onError(e.message))
              }
            >
              <FormIcon name="download" size={14} /> Download
            </button>
          </li>
        ))}
      </ul>
    );
  }
  const value = answer.value;
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return <span className="fm-empty-answer">No answer</span>;
  if (Array.isArray(value)) {
    return (
      <div className="fm-answer-tags">
        {value.map((v) => (
          <span key={v}>{v}</span>
        ))}
      </div>
    );
  }
  if (answer.type === 'email') return <a href={`mailto:${value}`}>{value}</a>;
  if (answer.type === 'url')
    return (
      <a href={value} target="_blank" rel="noreferrer noopener">
        {value}
      </a>
    );
  if (answer.type === 'phone') return <a href={`tel:${value}`}>{value}</a>;
  if (answer.type === 'date') return <span>{formatDay(value)}</span>;
  return <span className="fm-answer-text">{String(value)}</span>;
}

function SubmissionDrawer({ submission, onClose, onUpdate, onDelete, onError }) {
  const [notes, setNotes] = useState(submission.internal_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const late = submission.is_late ? daysLate(submission.created_at, submission.due_date) : 0;

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const saveNotes = async () => {
    setSavingNotes(true);
    const result = await onUpdate(submission.id, { internal_notes: notes });
    setSavingNotes(false);
    if (result) setSavedAt(new Date());
  };

  return (
    <div className="fm-drawer-backdrop" onClick={onClose} role="presentation">
      <aside className="fm-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Submission details">
        <header className="fm-drawer-head">
          <div>
            <span className="fm-eyebrow">Submission #{submission.id}</span>
            <h2>{submission.submitter_name || submission.submitter_email || 'Anonymous'}</h2>
            <p>
              {formatDateTime(submission.created_at)}
              {submission.client_name ? ` · ${submission.client_name}` : ''}
              {submission.project_name ? ` · ${submission.project_name}` : ''}
            </p>
          </div>
          <button type="button" className="fm-icon-btn" onClick={onClose} aria-label="Close">
            <FormIcon name="x" size={18} />
          </button>
        </header>

        <div className="fm-drawer-status">
          <SubmissionStatusPill status={submission.status} />
          {submission.is_late ? (
            <span className="fm-late">
              Late{late ? ` · ${late} day${late === 1 ? '' : 's'} after due date (${formatDay(submission.due_date)})` : ''}
            </span>
          ) : submission.due_date ? (
            <span className="fm-ontime">On time · due {formatDay(submission.due_date)}</span>
          ) : null}
          <div className="fm-drawer-status-actions">
            {submission.status === 'reviewed' ? (
              <button type="button" className="fm-btn fm-btn--sm" onClick={() => onUpdate(submission.id, { status: 'new' })}>
                Mark as new
              </button>
            ) : (
              <button type="button" className="fm-btn fm-btn--sm fm-btn--blue" onClick={() => onUpdate(submission.id, { status: 'reviewed' })}>
                <FormIcon name="check" size={14} /> Mark as reviewed
              </button>
            )}
          </div>
        </div>
        {submission.status === 'reviewed' && submission.reviewer_name ? (
          <p className="fm-hint fm-drawer-reviewed">
            Reviewed by {submission.reviewer_name} · {formatDateTime(submission.reviewed_at)}
          </p>
        ) : null}

        <div className="fm-drawer-body">
          <section className="fm-answers">
            {submission.answers.map((answer, i) =>
              answer.type === 'heading' ? (
                <h4 key={`h-${i}`} className="fm-answers-heading">
                  {answer.label}
                </h4>
              ) : (
                <div key={answer.key} className="fm-answer">
                  <dt>{answer.label}</dt>
                  <dd>
                    <AnswerValue answer={answer} onError={onError} />
                  </dd>
                </div>
              ),
            )}
          </section>

          <section className="fm-notes">
            <h4>
              <FormIcon name="lock" size={14} /> Internal notes
            </h4>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Only your team can see these notes." />
            <div className="fm-notes-actions">
              {savedAt ? <small>Saved {formatTime(savedAt)}</small> : <span />}
              <button type="button" className="fm-btn fm-btn--sm" onClick={saveNotes} disabled={savingNotes || notes === (submission.internal_notes || '')}>
                {savingNotes ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </section>

          {submission.automation_log?.length ? (
            <section className="fm-log">
              <h4>
                <FormIcon name="zap" size={14} /> Automation log
              </h4>
              <ul>
                {submission.automation_log.map((entry, i) => (
                  <li key={i} className={entry.ok ? '' : 'is-fail'}>
                    <span className="fm-log-dot" />
                    <span>
                      <strong>{entry.step.replace(/_/g, ' ')}</strong> — {entry.message}
                    </span>
                    <small>{formatTime(entry.at)}</small>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <dl className="fm-drawer-meta">
            <div>
              <dt>Source</dt>
              <dd>{submission.source === 'portal' ? 'Client portal' : submission.source === 'embed' ? 'Website embed' : 'Public link'}</dd>
            </div>
            {submission.submitter_email ? (
              <div>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${submission.submitter_email}`}>{submission.submitter_email}</a>
                </dd>
              </div>
            ) : null}
            {submission.ip_address ? (
              <div>
                <dt>IP address</dt>
                <dd>{submission.ip_address}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        <footer className="fm-drawer-foot">
          <button type="button" className="fm-btn fm-btn--outline-danger fm-btn--sm" onClick={onDelete}>
            <FormIcon name="trash" size={14} /> Delete submission
          </button>
        </footer>
      </aside>
    </div>
  );
}
